/**
 * AssignFirmUserResolution - Component for managing resolution status and firm user assignment
 *
 * Uses React Query mutation for updates.
 * Firm user list is loaded on-demand via FirmUserSelect (async paginated dropdown)
 * instead of a one-shot useEffect + totalCountEvent fetch.
 */

import React, { useEffect, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';

import { useUpdateMessageThread } from '../../hooks/useMessageMutations';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import FirmUserSelect from 'components/UI/FirmUserSelect/FirmUserSelect';
import { toFirmUserOptions } from 'hooks/queries/useFirmUsersDropdown';

function AssignFirmUserResolution({ isFromLead, messageDetails }) {
  const { addToast } = useToast();
  const updateThreadMutation = useUpdateMessageThread();

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      assign_to: [],
      need_resolution: false,
      resolved: false,
    },
  });

  useEffect(() => {
    if (messageDetails) {
      setValue('assign_to', toFirmUserOptions(messageDetails?.assign_to || []));
      setValue('need_resolution', messageDetails?.resolution_status || false);
      setValue('resolved', messageDetails?.resolved_status || false);
    }
  }, [messageDetails, setValue]);

  const onSubmit = useCallback(
    (data) => {
      const sendData = {
        sk: `message#${messageDetails?.message_thread_id}`,
        message_thread_id: messageDetails?.message_thread_id,
        case_id: messageDetails?.case?.case_id,
        case_name: messageDetails?.case?.case_name,
        is_new: false,
        is_read: true,
        subject: messageDetails?.subject,
        assign_to: data.assign_to || [],
        resolution_status: data?.need_resolution || false,
        resolved_status: data?.resolved || false,
      };

      return updateThreadMutation.mutate(
        { data: sendData, isLead: isFromLead },
        {
          onSuccess: () => {
            addToast(
              false,
              toastConstant.toasterType.SUCCESS,
              toastConstant.api.SUCCESS,
              toastConstant.message.SECURE_MESSAGE_THREAD_UPDATED_SUCCESS
            );
            reset({
              assign_to: toFirmUserOptions(data?.assign_to || []),
              need_resolution: data?.need_resolution || false,
              resolved: data?.resolved || false,
            });
          },
          onError: () => {
            addToast(
              false,
              toastConstant.toasterType.ERROR,
              toastConstant.api.FAILED,
              toastConstant.message.SECURE_MESSAGE_THREAD_UPDATED_FAILED
            );
          },
        }
      );
    },
    [messageDetails, isFromLead]
  );

  const handleCancel = useCallback(() => {
    reset({
      assign_to: toFirmUserOptions(messageDetails?.assign_to || []),
      need_resolution: messageDetails?.resolution_status || false,
      resolved: messageDetails?.resolved_status || false,
    });
  }, [reset, messageDetails]);

  if (!messageDetails) return null;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="d-flex align-items-center filter-wrap flex-wrap px-3 py-2 gap-2">
      <label className="p-medium text-muted mb-0">
        Assign to Firm User {(watch('need_resolution') || watch('resolved')) && <span className="text-danger">*</span>}
      </label>

      <div className="d-flex flex-column filter-wraper">
        <Controller
          name="assign_to"
          control={control}
          rules={{
            validate: (value) => {
              if ((watch('need_resolution') || watch('resolved')) && (!value || value.length === 0)) {
                return 'Assign to is Required';
              }
              return true;
            },
          }}
          render={({ field }) => (
            <FirmUserSelect
              id={field.name}
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder="Select a Firm User"
              isMulti
              isClearable={false}
              className="input-width"
              isSearchable={false}
            />
          )}
        />
        {errors?.assign_to && <InputErrorMessage className="mt-1">{errors.assign_to.message}</InputErrorMessage>}
      </div>

      {!messageDetails?.resolution_status ? (
        <div className="d-flex flex-column">
          <div className="d-flex align-items-center gap-2">
            <label className="p-medium text-muted mb-0">Need Resolution</label>
            <Controller
              name="need_resolution"
              control={control}
              render={({ field }) => (
                <InputSwitch
                  checked={field.value}
                  onChange={(e) => {
                    field.onChange(e.value);
                    if (!e.value) clearErrors('assign_to');
                  }}
                  className="input-swich"
                />
              )}
            />
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column">
          <div className="d-flex align-items-center">
            <label className="p-medium text-muted mb-0">Resolved</label>
            <Controller
              name="resolved"
              control={control}
              render={({ field }) => (
                <InputSwitch
                  checked={field.value}
                  onChange={(e) => {
                    field.onChange(e.value);
                    if (!e.value) clearErrors('assign_to');
                  }}
                  className="input-swich ms-2"
                />
              )}
            />
          </div>
        </div>
      )}

      {(isDirty || Object.keys(errors).length > 0) && (
        <div className="d-flex align-items-center">
          <Button
            label="Cancel"
            className="btn-outline p-button-secondary outline py-1 px-3 me-2 mt-1"
            type="button"
            style={{ overflow: 'inherit' }}
            onClick={handleCancel}
          />
          <Button
            label="Save"
            className="btn-outline p-button-primary p-button-secondary py-1 ps-3 mt-1"
            type="submit"
            loading={updateThreadMutation.isPending}
            disabled={updateThreadMutation.isPending}
          />
        </div>
      )}
    </form>
  );
}

AssignFirmUserResolution.propTypes = {
  isFromLead: PropTypes.bool,
  messageDetails: PropTypes.shape({
    sk: PropTypes.string,
    case_id: PropTypes.string,
    is_new: PropTypes.bool,
    is_read: PropTypes.bool,
    case_name: PropTypes.string,
    subject: PropTypes.string,
    accessible_list: PropTypes.array,
    assign_to: PropTypes.array,
    resolution_status: PropTypes.bool,
    resolved_status: PropTypes.bool,
  }),
};

AssignFirmUserResolution.defaultProps = {
  isFromLead: false,
  messageDetails: null,
};

export default memo(AssignFirmUserResolution);
